import {
  ApprovalResultSchema,
  ChangeRequestResultSchema,
  CreatePrivacyRequestSchema,
  CreateTrendTopicResultSchema,
  CursorPageSchema,
  IssuedSessionResponseSchema,
  PasskeyOptionsResponseSchema,
  ReauthResponseSchema,
  SessionUserSchema,
  LeadConversationDetailSchema,
  LeadConversationSchema,
  MapaPseudonymSchema,
  OperationStateSchema,
  PanelSnapshotSchema,
  PrivacyRequestDetailSchema,
  PrivacyRequestSchema,
  PrivacyResolutionRequestSchema,
  PrivacyResolutionResponseSchema,
  RedeemWhatsappCodeRequestSchema,
  PanelRunStatusSchema,
  RegenerateResultSchema,
  RejectResultSchema,
  UuidV4Schema,
  VerifyPrivacyRequestSchema,
  ViralRadarSchema,
  WhatsappLinkSchema,
  type ApiError,
  type ApprovalResultDto,
  type ApproveContentInput,
  type AuthenticationResponseJSON,
  type PasskeyOptionsResponse,
  type RegistrationResponseJSON,
  type SessionUser,
  type ChangeRequestResultDto,
  type CreatePrivacyRequest,
  type CreateTrendTopicResultDto,
  type CursorPage,
  type EmergencyPauseInput,
  type LeadConversationDetailDto,
  type LeadConversationDto,
  type OperationStateDto,
  type PanelRunStatusDto,
  type PanelSnapshotDto,
  type PrivacyRequestDetailDto,
  type PrivacyRequestDto,
  type PrivacyResolutionRequest,
  type PrivacyResolutionResponse,
  type RegenerateContentInput,
  type RegenerateResultDto,
  type RejectContentInput,
  type RejectResultDto,
  type RequestContentChangeInput,
  type VerifyPrivacyRequest,
  type ViralRadarDto,
  type WhatsappLinkDto,
} from '@devmil/contracts';

import {
  PanelApiError,
  type AuthSessionDto,
  type PanelAuthPort,
  type PanelDataSource,
  type PanelMediaTarget,
  type RedeemWhatsappCodeInput,
} from './PanelDataSource.js';

/**
 * Fonte de dados por HTTP.
 *
 * Ela troca a fixture SEM que nada visual mude: a tela recebe exatamente a
 * mesma forma, validada pelo MESMO schema Zod que a fixture já passa. É por
 * isso que a fixture é validada em teste; ela não é um mock solto, é um ensaio
 * do formato real.
 *
 * Duas regras que valem para todo método daqui:
 *
 * 1. **Resposta de fora é entrada não confiável.** Nada é usado antes de passar
 *    pelo schema. Um campo a mais vindo da API é erro, e não um extra
 *    inofensivo: `strictObject` recusa.
 * 2. **Cookie de sessão viaja, e o token CSRF acompanha toda escrita.** Sem
 *    `credentials: 'include'` o navegador não manda o cookie `__Host-`, e a
 *    API responderia 401 sem que ninguém entendesse por quê.
 */

export interface HttpPanelDataSourceOptions {
  baseUrl: string;
  /** Injetável para teste. Em produção é o `fetch` do navegador. */
  fetchImpl?: typeof fetch;
}

const ERRO_GENERICO = (status: number): ApiError => ({
  code: status >= 500 ? 'INTERNAL_ERROR' : 'REQUEST_FAILED',
  message:
    status >= 500
      ? 'O servidor falhou. Tente de novo em instantes.'
      : 'A requisição não pôde ser concluída.',
  requestId: 'desconhecido',
  fieldErrors: null,
});

export class HttpPanelDataSource implements PanelDataSource {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  /**
   * Token CSRF da sessão vigente, em memória e NUNCA em disco.
   *
   * Ele não gira a cada leitura: a API o deriva do token de sessão
   * (`derivarCsrf` em `auth-service.ts`), então ler duas vezes devolve o mesmo
   * valor. Quem o gira é o refresh, que troca o token de sessão e devolve o
   * novo no envelope.
   */
  private csrfToken: string | null = null;

  /**
   * Porta de autenticação, presente porque esta fonte fala com a API real.
   *
   * As cerimônias de login e de cadastro NÃO levam token CSRF, e não é
   * esquecimento: o token nasce com a sessão (`GET /v1/auth/csrf` exige sessão
   * viva), então antes do login ele não existe por construção. A API amarra
   * essas rotas de outro jeito: desafio de uso único mais verificação de
   * origem dentro da cerimônia WebAuthn.
   *
   * As QUATRO rotas de sessão (`session/refresh`, `reauth/options`,
   * `reauth/verify`, `logout`) levam o token, e a API passou a exigi-lo nelas.
   * O comentário anterior dizia que buscar o token ali "giraria o token no meio
   * de uma escrita em voo": era verdade quando a emissão sorteava um valor
   * novo, e deixou de ser quando ela virou leitura pura. Hoje uma leitura não
   * mata nada, e o único giro é o do próprio refresh, que devolve o valor novo
   * na mesma resposta.
   */
  readonly auth: PanelAuthPort;

  constructor(options: HttpPanelDataSourceOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/u, '');
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.auth = this.montarPortaDeAuth();
  }

  /**
   * Guarda o token CSRF do envelope de sessão e devolve o resto.
   *
   * O token fica AQUI, em memória do adaptador, e não sobe para a tela: o que
   * sobe acaba em prop, em estado de depuração e em relatório de erro.
   */
  private guardarSessaoEmitida(dado: unknown): AuthSessionDto {
    const {csrfToken, user, expiresAt, idleExpiresAt} =
      IssuedSessionResponseSchema.parse(dado);
    this.csrfToken = csrfToken;
    return {user, expiresAt, idleExpiresAt};
  }

  private montarPortaDeAuth(): PanelAuthPort {
    const post = <T>(
      caminho: string,
      corpo: unknown,
      parse: (d: unknown) => T,
    ) =>
      this.pedir(caminho, {method: 'POST', body: JSON.stringify(corpo)}, parse);

    /** As rotas de sessão: mesma coisa, com o token da sessão vigente. */
    const postDeSessao = async <T>(
      caminho: string,
      corpo: unknown,
      parse: (d: unknown) => T,
    ): Promise<T> => {
      const resposta = await this.escreverNaSessao(caminho, corpo);
      return parse(await resposta.json());
    };

    return {
      loginOptions: (email: string): Promise<PasskeyOptionsResponse> =>
        post('/v1/auth/login/options', {email}, (dado) =>
          PasskeyOptionsResponseSchema.parse(dado),
        ),

      loginVerify: (input: {
        ceremonyId: string;
        response: AuthenticationResponseJSON;
      }): Promise<AuthSessionDto> =>
        post('/v1/auth/login/verify', input, (dado) =>
          this.guardarSessaoEmitida(dado),
        ),

      // O convite viaja SÓ no corpo. A rota real aceita exatamente
      // `{enrollmentToken}` e depois `{ceremonyId, response}`: o contrato
      // `BootstrapVerifyRequestSchema` pede o convite de novo no verify, mas a
      // rota (strictObject) recusaria o campo extra; quem manda é a rota.
      bootstrapOptions: (
        enrollmentToken: string,
      ): Promise<PasskeyOptionsResponse> =>
        post('/v1/auth/bootstrap/options', {enrollmentToken}, (dado) =>
          PasskeyOptionsResponseSchema.parse(dado),
        ),

      bootstrapVerify: (input: {
        ceremonyId: string;
        response: RegistrationResponseJSON;
      }): Promise<AuthSessionDto> =>
        post('/v1/auth/bootstrap/verify', input, (dado) =>
          this.guardarSessaoEmitida(dado),
        ),

      reauthOptions: (): Promise<PasskeyOptionsResponse> =>
        postDeSessao('/v1/auth/reauth/options', undefined, (dado) =>
          PasskeyOptionsResponseSchema.parse(dado),
        ),

      reauthVerify: (input: {
        ceremonyId: string;
        response: AuthenticationResponseJSON;
      }): Promise<{reauthenticatedUntil: string}> =>
        postDeSessao('/v1/auth/reauth/verify', input, (dado) =>
          ReauthResponseSchema.parse(dado),
        ),

      // O refresh gira o token de sessão, e com ele o token CSRF. O que chega
      // aqui já é o valor NOVO, e `guardarSessaoEmitida` o guarda no lugar do
      // que acabou de ser usado: sem essa troca, a escrita seguinte iria com o
      // token de uma sessão revogada.
      refreshSession: (): Promise<AuthSessionDto> =>
        postDeSessao('/v1/auth/session/refresh', undefined, (dado) =>
          this.guardarSessaoEmitida(dado),
        ),

      me: (): Promise<SessionUser> =>
        this.pedir('/v1/me', {method: 'GET'}, (dado) =>
          SessionUserSchema.parse(dado),
        ),

      // 204 sem corpo: `pedir` chamaria `json()` no vazio e falharia depois do
      // logout já feito. O token em memória morre junto, senão a próxima
      // sessão herdaria um token de sessão revogada e toda escrita daria 403.
      // O `finally` existe porque a rota passou a exigir sessão viva: sair com
      // sessão já vencida levanta 401, e deixar o token velho em memória depois
      // disso seria guardar lixo de uma sessão que ninguém mais tem.
      logout: async (): Promise<void> => {
        try {
          await this.escreverNaSessao('/v1/auth/logout', undefined);
        } finally {
          this.csrfToken = null;
        }
      },
    };
  }

  /**
   * Uma escrita nas rotas de SESSÃO, com o token e com UMA repetição.
   *
   * O token vem sempre de `token()`, e nunca do que está em memória. Não é
   * preciosismo: o token é de memória e some num F5, enquanto o cookie
   * sobrevive. Quem lesse a memória mandaria `undefined` na primeira renovação
   * depois de qualquer recarga de página, levaria 403, e a sessão do dono
   * morreria calada no vencimento por ociosidade, com o painel mostrando
   * conteúdo de uma sessão já morta.
   *
   * A repetição é a mesma de `escrever`, pelo mesmo motivo e com a mesma
   * segurança: `requireTrustedOrigin`, `requireOwner` e `requireCsrf` são
   * `preHandler`, então a requisição recusada não chegou ao handler e não teve
   * efeito nenhum — nem consumiu o desafio de uso único do `reauth/verify`. Ela
   * cobre a corrida de duas abas: a que perde busca o token de novo, agora o da
   * sessão que a outra acabou de abrir.
   *
   * Falha ao BUSCAR o token não repete, de propósito: `GET /v1/auth/csrf` só
   * responde 401 quando não há sessão viva para este navegador, e repetir isso
   * é gastar requisição para reaprender a mesma coisa. O 401 sobe, que é o que
   * o guardião de sessão precisa para mostrar a tela de entrada.
   */
  private async escreverNaSessao(
    caminho: string,
    corpo: unknown,
  ): Promise<Response> {
    const tentar = (token: string): Promise<Response> =>
      this.executar(caminho, {
        method: 'POST',
        headers: {'x-csrf-token': token},
        ...(corpo === undefined ? {} : {body: JSON.stringify(corpo)}),
      });

    // FORA do `try` de propósito: aqui dentro, um 401 da leitura do token cairia
    // na mesma lista de repetição e viraria duas leituras para aprender o mesmo.
    const token = await this.token();
    try {
      return await tentar(token);
    } catch (erro: unknown) {
      if (!HttpPanelDataSource.recusadaNoGuarda(erro)) {
        throw erro;
      }
      return tentar(await this.token());
    }
  }

  private async executar(
    caminho: string,
    init: RequestInit,
  ): Promise<Response> {
    const resposta = await this.fetchImpl(`${this.baseUrl}${caminho}`, {
      ...init,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(init.body ? {'content-type': 'application/json'} : {}),
        ...(init.headers ?? {}),
      },
    });

    if (!resposta.ok) {
      // O envelope de erro da API é o mesmo em toda rota. Quando ele não vier,
      // um genérico entra no lugar: a tela precisa de um caminho só.
      let payload: ApiError;
      try {
        payload = (await resposta.json()) as ApiError;
      } catch {
        payload = ERRO_GENERICO(resposta.status);
      }
      throw new PanelApiError(payload, resposta.status);
    }

    return resposta;
  }

  private async pedir<T>(
    caminho: string,
    init: RequestInit,
    parse: (dado: unknown) => T,
  ): Promise<T> {
    const resposta = await this.executar(caminho, init);
    return parse(await resposta.json());
  }

  /**
   * Monta um segmento de caminho a partir de identificador validado.
   *
   * Recusar, e não limpar: se o valor não casa com o formato do contrato, a
   * requisição não sai. Escapar um pseudônimo estranho para caber na URL seria
   * o mesmo jogo de gato e rato que o repositório já decidiu não jogar, e
   * bastaria um formato novo para abrir caminho para outra rota.
   */
  private static segmento(
    valor: string,
    esquema: {parse: (dado: unknown) => string},
  ): string {
    return encodeURIComponent(esquema.parse(valor));
  }

  /**
   * Busca o token CSRF da sessão vigente.
   *
   * Cada escrita pega o seu. Não é porque o token gire a cada emissão (ele não
   * gira: a API o deriva do token de sessão, e duas leituras devolvem o mesmo
   * valor), e sim porque quem gira é o REFRESH, que pode acontecer em outra aba
   * a qualquer momento. Ler antes de escrever é o que faz a escrita usar o
   * token da sessão que existe agora, e não o da que existia quando esta
   * instância foi construída — que, depois de um F5, é nenhuma.
   */
  private async token(): Promise<string> {
    const {csrfToken} = await this.pedir(
      '/v1/auth/csrf',
      {method: 'GET'},
      (dado) => dado as {csrfToken: string},
    );
    this.csrfToken = csrfToken;
    return csrfToken;
  }

  /**
   * A recusa veio de um guarda de requisição, antes do handler da rota?
   *
   * Só estes dois códigos, em lista de permissão: `CSRF_INVALID` (403) e
   * `AUTH_REQUIRED` (401). São exatamente o que a corrida com a renovação de
   * sessão produz. O guardião gira a sessão 5 minutos antes do vencimento por
   * ociosidade, e o giro REVOGA a sessão anterior junto com o token CSRF dela
   * (`refreshSession` em `auth-service.ts`); uma escrita que buscou o token um
   * instante antes chega com o token da sessão morta (403) ou com o cookie
   * antigo (401). Nos dois casos a sessão está perfeitamente viva, e a
   * mensagem que a tela mostraria, "esta sessão não tem permissão para esta
   * ação", culparia o papel do usuário por uma corrida de milissegundos.
   */
  private static recusadaNoGuarda(erro: unknown): boolean {
    if (!(erro instanceof PanelApiError)) {
      return false;
    }
    return (
      (erro.status === 403 && erro.payload.code === 'CSRF_INVALID') ||
      (erro.status === 401 && erro.payload.code === 'AUTH_REQUIRED')
    );
  }

  /**
   * Uma escrita, com UMA repetição quando o guarda recusou.
   *
   * Repetir aqui é seguro POR CONSTRUÇÃO, e não por otimismo: `requireCsrf` e
   * `requireOwner` são `preHandler` no Fastify, então a requisição recusada
   * nunca alcançou o handler e não teve efeito nenhum. A repetição busca token
   * novo, que já é o da sessão nova.
   *
   * Uma só, e nunca em laço: se a segunda também for recusada, a sessão morreu
   * de verdade (ou a origem não bate) e o erro sobe como sempre, para o
   * guardião de sessão e para a tela decidirem. Repetição em laço transformaria
   * sessão vencida em espera infinita.
   */
  private async escrever<T>(
    caminho: string,
    corpo: unknown,
    parse: (dado: unknown) => T,
  ): Promise<T> {
    const tentar = async (): Promise<T> => {
      const token = await this.token();
      return this.pedir(
        caminho,
        {
          method: 'POST',
          body: JSON.stringify(corpo),
          headers: {'x-csrf-token': token},
        },
        parse,
      );
    };

    try {
      return await tentar();
    } catch (erro: unknown) {
      if (!HttpPanelDataSource.recusadaNoGuarda(erro)) {
        throw erro;
      }
      return tentar();
    }
  }

  /** Leitura simples. `signal` só entra no objeto quando existe, porque
   * `exactOptionalPropertyTypes` recusa `signal: undefined`. */
  private async ler<T>(
    caminho: string,
    parse: (dado: unknown) => T,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.pedir(
      caminho,
      signal ? {method: 'GET', signal} : {method: 'GET'},
      parse,
    );
  }

  async getSnapshot(signal?: AbortSignal): Promise<PanelSnapshotDto> {
    return this.ler(
      '/v1/panel/snapshot',
      (dado) => PanelSnapshotSchema.parse(dado),
      signal,
    );
  }

  /**
   * O endereço da capa ou do vídeo daquela versão.
   *
   * Quem busca é o `<img>` ou o `<video>`, e não este adaptador: por isso o
   * método devolve texto e não passa por `pedir`. O cookie de sessão viaja
   * mesmo assim porque em produção a base é `/`, ou seja, MESMA ORIGEM que
   * serviu o painel (o `panel.Caddyfile` encaminha `/v1/*` para a API), e
   * requisição de mesma origem leva o cookie `__Host-` sem ninguém pedir.
   *
   * Com uma base absoluta apontando para outra origem, que é o arranjo de
   * desenvolvimento, o navegador NÃO manda o cookie num `<img>`, e a rota
   * responde 401. Isso é limite conhecido do modo de desenvolvimento, não de
   * produção, e está escrito em `docs/architecture/panel-data-source.md`.
   *
   * O identificador é escapado antes de entrar no caminho. Ele já vem do
   * snapshot, que passou por `UuidV4Schema`, mas montar caminho com texto sem
   * escapar é o hábito que um dia encontra um valor com barra dentro.
   */
  mediaUrl(contentVersionId: string, role: PanelMediaTarget): string | null {
    const id = encodeURIComponent(contentVersionId);
    /*
     * A POSIÇÃO entra no caminho como número, e ela é conferida aqui antes.
     *
     * O servidor confere de novo (`findPanelMediaObject` recusa fora do
     * intervalo), e as duas conferências existem pelo mesmo motivo de sempre:
     * esta dá a resposta boa (nenhum endereço, e a tela cai no esquema), aquela
     * é a que não tem exceção.
     */
    if (typeof role === 'object') {
      return Number.isInteger(role.slide) && role.slide >= 1
        ? `${this.baseUrl}/v1/panel/content/${id}/slides/${role.slide}`
        : null;
    }
    return `${this.baseUrl}/v1/panel/content/${id}/${role}`;
  }

  async approveContent(input: ApproveContentInput): Promise<ApprovalResultDto> {
    return this.escrever('/v1/panel/approve', input, (dado) =>
      ApprovalResultSchema.parse(dado),
    );
  }

  async requestChange(
    input: RequestContentChangeInput,
  ): Promise<ChangeRequestResultDto> {
    return this.escrever('/v1/panel/request-change', input, (dado) =>
      ChangeRequestResultSchema.parse(dado),
    );
  }

  async rejectContent(input: RejectContentInput): Promise<RejectResultDto> {
    return this.escrever('/v1/panel/reject', input, (dado) =>
      RejectResultSchema.parse(dado),
    );
  }

  /**
   * Regenerar, pelo mesmo `escrever` das outras escritas.
   *
   * Ele já busca o token CSRF e repete uma vez quando o guarda recusa. O que
   * ele NÃO resolve é a passkey recente: a rota responde `403 REAUTH_REQUIRED`,
   * `recusadaNoGuarda` não reconhece esse código de propósito, e o erro sobe
   * para o provider abrir o diálogo de reautenticação. Repetir aqui só gastaria
   * uma requisição para tomar o mesmo 403.
   */
  async getRunStatus(
    runId: string,
    signal?: AbortSignal,
  ): Promise<PanelRunStatusDto> {
    return this.ler(
      `/v1/panel/runs/${encodeURIComponent(runId)}`,
      (dado) => PanelRunStatusSchema.parse(dado),
      signal,
    );
  }

  async regenerateContent(
    input: RegenerateContentInput,
  ): Promise<RegenerateResultDto> {
    return this.escrever('/v1/panel/regenerate', input, (dado) =>
      RegenerateResultSchema.parse(dado),
    );
  }

  async setEmergencyPause(
    input: EmergencyPauseInput,
  ): Promise<OperationStateDto> {
    return this.escrever('/v1/panel/emergency-pause', input, (dado) =>
      OperationStateSchema.parse(dado),
    );
  }

  // -------------------------------------------------------------------------
  // Funil MAPA
  // -------------------------------------------------------------------------

  async listLeadConversations(
    signal?: AbortSignal,
  ): Promise<CursorPage<LeadConversationDto>> {
    return this.ler(
      '/v1/leads',
      (dado) => CursorPageSchema(LeadConversationSchema).parse(dado),
      signal,
    );
  }

  async getLeadConversation(
    pseudonym: string,
    signal?: AbortSignal,
  ): Promise<LeadConversationDetailDto> {
    const alvo = HttpPanelDataSource.segmento(pseudonym, MapaPseudonymSchema);
    return this.ler(
      `/v1/leads/${alvo}`,
      (dado) => LeadConversationDetailSchema.parse(dado),
      signal,
    );
  }

  /**
   * Resgate manual do código.
   *
   * Código e telefone são validados AQUI antes de sair, no mesmo schema do
   * contrato: uma requisição que já nasce fora do formato só serviria para
   * gastar uma das poucas tentativas que a API permite antes do 429.
   */
  async redeemWhatsappCode({
    pseudonym,
    ...corpo
  }: RedeemWhatsappCodeInput): Promise<WhatsappLinkDto> {
    const alvo = HttpPanelDataSource.segmento(pseudonym, MapaPseudonymSchema);
    return this.escrever(
      `/v1/leads/${alvo}/whatsapp-redeem`,
      RedeemWhatsappCodeRequestSchema.parse(corpo),
      (dado) => WhatsappLinkSchema.parse(dado),
    );
  }

  // -------------------------------------------------------------------------
  // Direitos do titular
  // -------------------------------------------------------------------------

  async listPrivacyRequests(
    signal?: AbortSignal,
  ): Promise<CursorPage<PrivacyRequestDto>> {
    return this.ler(
      '/v1/privacy/requests',
      (dado) => CursorPageSchema(PrivacyRequestSchema).parse(dado),
      signal,
    );
  }

  async createPrivacyRequest(
    input: CreatePrivacyRequest,
  ): Promise<PrivacyRequestDto> {
    return this.escrever(
      '/v1/privacy/requests',
      CreatePrivacyRequestSchema.parse(input),
      (dado) => PrivacyRequestSchema.parse(dado),
    );
  }

  async getPrivacyRequest(
    requestId: string,
    signal?: AbortSignal,
  ): Promise<PrivacyRequestDetailDto> {
    const alvo = HttpPanelDataSource.segmento(requestId, UuidV4Schema);
    return this.ler(
      `/v1/privacy/requests/${alvo}`,
      (dado) => PrivacyRequestDetailSchema.parse(dado),
      signal,
    );
  }

  async verifyPrivacyRequest(
    requestId: string,
    input: VerifyPrivacyRequest,
  ): Promise<PrivacyRequestDto> {
    const alvo = HttpPanelDataSource.segmento(requestId, UuidV4Schema);
    return this.escrever(
      `/v1/privacy/requests/${alvo}/verify`,
      VerifyPrivacyRequestSchema.parse(input),
      (dado) => PrivacyRequestSchema.parse(dado),
    );
  }

  async resolvePrivacyRequest(
    requestId: string,
    input: PrivacyResolutionRequest,
  ): Promise<PrivacyResolutionResponse> {
    const alvo = HttpPanelDataSource.segmento(requestId, UuidV4Schema);
    return this.escrever(
      `/v1/privacy/requests/${alvo}/resolve`,
      PrivacyResolutionRequestSchema.parse(input),
      (dado) => PrivacyResolutionResponseSchema.parse(dado),
    );
  }

  // -------------------------------------------------------------------------
  // Radar de virais
  // -------------------------------------------------------------------------

  /**
   * O radar do dia.
   *
   * A resposta passa pelo `ViralRadarSchema` como qualquer outra, e aqui isso
   * pesa mais que no resto: o corpo carrega legenda de terceiro, que é entrada
   * não confiável por definição. O schema é o que garante que ela chegue como
   * texto de tamanho conhecido, num campo previsto, e não como um objeto que
   * alguém desenhou do outro lado.
   */
  async getViralRadar(signal?: AbortSignal): Promise<ViralRadarDto> {
    return this.ler(
      '/v1/panel/radar',
      (dado) => ViralRadarSchema.parse(dado),
      signal,
    );
  }

  /**
   * Virar pauta: escrita comum, com o identificador escapado no caminho.
   *
   * O corpo é vazio porque não há nada a escolher: a moldura da premissa, o
   * prazo e o tipo de prova são decisão do serviço, e não do painel. Mandar
   * qualquer um deles daqui abriria caminho para a tela ditar a pauta.
   */
  async createTrendTopic(
    viralPostId: string,
  ): Promise<CreateTrendTopicResultDto> {
    const alvo = HttpPanelDataSource.segmento(viralPostId, UuidV4Schema);
    return this.escrever(`/v1/panel/radar/${alvo}/topic`, {}, (dado) =>
      CreateTrendTopicResultSchema.parse(dado),
    );
  }

  /** Só para teste: diz qual token está em memória. */
  get tokenEmMemoria(): string | null {
    return this.csrfToken;
  }
}
